import { useCreateOrderQuoteGuestAcceptanceLinkMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export const GUEST_EMAIL_RE = /^\S+@\S+\.\S+$/;

interface SendGuestLinkModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderQuoteId: string;
	/** Emails that already hold an active link — resending replaces theirs. */
	activeEmails?: string[];
	onSuccess: () => void;
}

/**
 * Mints a password-less acceptance link for one email address.
 *
 * A modal rather than an inline form: this lives in the metadata rail, where
 * there isn't room to explain what the link actually grants.
 */
export function SendGuestLinkModal({
	open,
	onOpenChange,
	orderQuoteId,
	activeEmails = [],
	onSuccess,
}: SendGuestLinkModalProps) {
	const [email, setEmail] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [createLink, { loading }] = useCreateOrderQuoteGuestAcceptanceLinkMutation();

	useEffect(() => {
		if (open) {
			setEmail('');
			setError(null);
		}
	}, [open]);

	const trimmed = email.trim();
	const valid = GUEST_EMAIL_RE.test(trimmed);
	// Resending to an address supersedes its existing link, so warn before it happens.
	const supersedes = activeEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase());

	const handleSubmit = async () => {
		if (!valid || loading) return;
		setError(null);
		try {
			const { data } = await createLink({
				variables: { input: { orderQuoteId, email: trimmed } },
			});
			const res = data?.createOrderQuoteGuestAcceptanceLink;
			// Failures come back as `success: false` with a reason, not as a throw.
			if (!res?.success) {
				setError(res?.message || 'Failed to create the link.');
				return;
			}
			toast.success(`Acceptance link sent to ${res.email ?? trimmed}`);
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to create the link.');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Send guest link</DialogTitle>
					<DialogDescription>
						Lets someone without an account review and accept this quote. The link is
						emailed to the address below, and opening it needs a code sent to that same
						address.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-1.5">
							<Label>
								Email address <span className="text-destructive">*</span>
							</Label>
							<Input
								autoFocus
								type="email"
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									if (error) setError(null);
								}}
								placeholder="name@company.com"
								disabled={loading}
							/>
							{error ? (
								<div className="text-sm text-status-danger">{error}</div>
							) : supersedes ? (
								<div className="text-sm text-status-warn">
									This address already has a link. Sending again replaces it — the
									old one stops working.
								</div>
							) : (
								<div className="text-sm text-text-4">
									The link covers the whole quote, not a single offer. It stays
									valid until you revoke it.
								</div>
							)}
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!valid || loading}>
							{loading ? 'Sending…' : 'Send link'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
